import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { publicApi } from '../../api/client';
import { serviceProducts, getProductImage } from '../../data/serviceProducts';
import { interpretingServices } from '../../data/interpretingServices';
import { getServiceTypeImage } from '../../data/navServices';
import ServicePriceRows from '../common/ServicePriceRows';
import assetUrl from '../../assetUrl';
import './FeaturedDocs.css';

// Cartes statiques de secours si l'API est injoignable (affichées sans prix)
const FALLBACK_PRODUCTS = ['birth', 'diploma', 'passport', 'criminal'];
const FALLBACK_INTERPS = ['simultaneous', 'consecutive', 'businessInterpreting', 'remoteInterpreting'];

// L'admin choisit l'ordre via homeOrder (1 = premier), puis popularité
const byHomeOrder = (a, b) => {
  const pa = Number(a.homeOrder) || 0;
  const pb = Number(b.homeOrder) || 0;
  if (pa && pb) return pa - pb;
  if (pa) return -1;
  if (pb) return 1;
  return (b.orders || 0) - (a.orders || 0);
};

export default function FeaturedDocs() {
  const { t, lang } = useLanguage();
  const f = t.homeFeatured;
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const trackRef = useRef(null);
  const timerRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [state, setState] = useState({ services: [], loading: true, offline: false });

  useEffect(() => {
    let alive = true;
    publicApi.catalog()
      .then((res) => { if (alive) setState({ services: res.services || [], loading: false, offline: false }); })
      .catch(() => { if (alive) setState({ services: [], loading: false, offline: true }); });
    return () => { alive = false; };
  }, []);

  const cards = state.offline
    ? [
        ...FALLBACK_PRODUCTS.map((id) => {
          const p = serviceProducts.find((x) => x.id === id);
          if (!p) return null;
          return { id, title: t.serviceCatalog?.products?.[id]?.title || p.slug, image: getProductImage(p), offline: true };
        }),
        ...FALLBACK_INTERPS.map((slug) => {
          const s = interpretingServices.find((x) => x.slug === slug);
          if (!s) return null;
          const title = lang === 'ar' ? (s.nameAr || s.name) : lang === 'en' ? (s.nameEn || s.name) : s.name;
          return { id: slug, title, image: s.image || getServiceTypeImage(slug), offline: true };
        }),
      ].filter(Boolean)
    : state.services.filter((s) => s.featured).sort(byHomeOrder).map((s) => {
        const rating = s.reviewsCount || 0;
        const title = lang === 'ar'
          ? (s.titleAr || s.titleFr)
          : lang === 'en'
            ? (s.titleEn || s.titleFr)
            : s.titleFr;
        return {
          id: s.slug,
          image: s.image,
          title,
          price: s.basePrice || (s.prices && Object.values(s.prices)[0]) || 0,
          unit: s.unit,
          rating,
          reviewLabel: rating.toLocaleString(locale),
          svc: s,
        };
      });

  const scroll = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    if (dir === 1 && track.scrollLeft >= max - 10) {
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (dir === -1 && track.scrollLeft <= 10) {
      track.scrollTo({ left: max, behavior: 'smooth' });
    } else {
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(() => scroll(1), 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, scroll]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') scroll(-1);
      if (e.key === 'ArrowRight') scroll(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scroll]);

  if (state.loading || !cards.length) return null;

  return (
    <section className="section featured-docs">
      <div className="container">
        <div className="section-header">
          <h2>{f.title}</h2>
          <p>{f.subtitle}</p>
        </div>

        <div
          className="featured-docs-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            type="button"
            className="featured-docs-arrow prev"
            onClick={() => scroll(-1)}
            aria-label="Précédent"
          >
            <ChevronLeft size={22} />
          </button>

          <div
            className="featured-docs-track"
            ref={trackRef}
          >
            {cards.map((card) => (
              <div key={card.id} className="featured-doc-card">
                <div className="featured-doc-media">
                  <img
                    src={assetUrl(card.image)}
                    alt={card.title}
                    loading="lazy"
                    decoding="async"
                  />
                  <Link to="/order" className="asc-order" aria-label="Commander en ligne">
                    <span>Commander</span>
                  </Link>
                </div>

                <div className="featured-doc-body">
                  <h3>{card.title}</h3>
                  {!card.offline && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', gap: '2px', color: '#f5b041' }}>
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={14} fill={card.rating > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                          ))}
                        </div>
                        <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                          {card.reviewLabel} {t.serviceCatalog.reviews}
                        </span>
                      </div>
                      <ServicePriceRows
                        service={card.svc || { price: card.price, unit: card.unit }}
                        locale={locale}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="featured-docs-arrow next"
            onClick={() => scroll(1)}
            aria-label="Suivant"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="featured-docs-more">
          <Link to="/tous-les-services" className="btn btn-outline">
            {f.seeMore}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
